import { ref, nextTick, type Ref } from "vue";
import QRCode from "qrcode";
import { saveAs } from "file-saver";
import { localStorageGetShopCode } from "@/common/localStorageUtil";
import type { QrSizeKey } from "../types";

/**
 * 仮会員QRコード画面のQR描画・出力
 * 3サイズ（sm/md/lg）のcanvasへのQRコード描画、PNGダウンロード、印刷を提供する。
 * Render（描画）と Export（出力）は canvas/QRコード出力という同じ関心事のため統合している。
 *
 * @param deps.registerUrl - 仮会員登録URL（QRコードに埋め込む）
 * @param deps.selectedShopCode - 選択中の店舗コード（ダウンロードファイル名に使用）
 * @param deps.showError - エラーダイアログ表示関数
 *
 * @returns qrDispSmRef - 小サイズ（60px表示）canvas参照
 * @returns qrDispMdRef - 中サイズ（120px表示）canvas参照
 * @returns qrDispLgRef - 大サイズ（180px表示）canvas参照
 * @returns renderAllQrs - 全サイズのQR描画を実行
 * @returns downloadPng - 指定サイズのQRコードPNGダウンロード
 * @returns printQr - QRコード印刷（lgサイズcanvasを複製→body直下に配置→印刷→復元）
 */
export const useTmpMemberQrCodeQr = (deps: {
  registerUrl: Ref<string>;
  selectedShopCode: Ref<string | null>;
  showError: (msg: string) => void;
}) => {
  // QRキャンバスへの参照
  const qrDispSmRef = ref<HTMLCanvasElement | null>(null);
  const qrDispMdRef = ref<HTMLCanvasElement | null>(null);
  const qrDispLgRef = ref<HTMLCanvasElement | null>(null);

  /**
   * 個別canvasへのQR描画（内部ヘルパー）
   */
  const renderQr = async (canvasEl: HTMLCanvasElement, size: number): Promise<void> => {
    await QRCode.toCanvas(canvasEl, deps.registerUrl.value, {
      width: size,
      margin: 2,
      errorCorrectionLevel: "M",
    });
  };

  /**
   * 全canvasのクリア（内部ヘルパー）
   */
  const clearCanvas = (): void => {
    const refs = [qrDispSmRef.value, qrDispMdRef.value, qrDispLgRef.value];
    refs.forEach((c) => {
      if (!c) return;
      const ctx = c.getContext("2d");
      if (ctx) ctx.clearRect(0, 0, c.width, c.height);
    });
  };

  /**
   * 全サイズのQR描画を実行
   * nextTick でDOM更新を待機後に各canvasへ描画する
   */
  const renderAllQrs = async (): Promise<void> => {
    clearCanvas();
    if (!deps.registerUrl.value) return;

    // DOM更新を終えるまで待つ
    await nextTick();

    if (qrDispSmRef.value) await renderQr(qrDispSmRef.value, 60);
    if (qrDispMdRef.value) await renderQr(qrDispMdRef.value, 120);
    if (qrDispLgRef.value) await renderQr(qrDispLgRef.value, 180);
  };

  /**
   * 指定サイズのQRコードPNGダウンロード
   * 表示canvasとは別にダウンロード用canvasを生成（200/400/600px）
   */
  const downloadPng = async (sizeKey: QrSizeKey): Promise<void> => {
    // ダウンロード用canvasを内部的に生成
    let size = 200;
    if (sizeKey === "md") size = 400;
    if (sizeKey === "lg") size = 600;
    const canvas = document.createElement("canvas");
    try {
      await QRCode.toCanvas(canvas, deps.registerUrl.value, {
        width: size,
        margin: 2,
        errorCorrectionLevel: "M",
      });
      const filename = `tmp-member-qr-${
        deps.selectedShopCode.value || localStorageGetShopCode()
      }-${sizeKey}.png`;
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/png")
      );
      if (blob) {
        saveAs(blob, filename);
        return;
      }
      // toBlob が null の場合のフォールバック
      const url = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e: unknown) {
      deps.showError("QRコードのダウンロードに失敗しました。");
    }
  };

  /**
   * QRコード印刷
   * lgサイズcanvasをbody直下に複製→#appを非表示→印刷→afterprintで復元
   */
  const printQr = (): void => {
    const appEl = document.getElementById("app");
    appEl?.classList.add("hidden-print");

    // canvasを一時的にbody直下へコピー
    const canvas = qrDispLgRef.value;
    if (!canvas) return;

    const clonedCanvas = canvas.cloneNode(true) as HTMLCanvasElement;
    clonedCanvas.width = canvas.width;
    clonedCanvas.height = canvas.height;
    const ctx = clonedCanvas.getContext("2d");
    if (ctx) {
      ctx.drawImage(canvas, 0, 0);
    }
    clonedCanvas.classList.add("disp-print");
    document.body.appendChild(clonedCanvas);

    const restoreCanvas = () => {
      // コピーしたcanvasを削除
      if (clonedCanvas && clonedCanvas.parentNode) {
        clonedCanvas.parentNode.removeChild(clonedCanvas);
      }
      appEl?.classList.remove("hidden-print");
      window.removeEventListener("afterprint", restoreCanvas);
    };
    window.addEventListener("afterprint", restoreCanvas);

    window.print();
  };

  return {
    qrDispSmRef,
    qrDispMdRef,
    qrDispLgRef,
    renderAllQrs,
    downloadPng,
    printQr,
  };
};
